import className from 'classnames/bind';
import classNames from 'classnames';
import styles from './ContentWrapper.module.scss';
import { getReactiveContent, isFromOrganicSearch } from "@/lib/utils/helpers";
import { useLightboxStore } from "@/lib/stores/lightbox";
import PropTypes from "prop-types";
import Loading from "@/components/Loading/Loading";
import { Suspense, useEffect, useId, useState, startTransition, useRef } from "react";
import { twMerge } from 'tailwind-merge';
import { useAuth } from '@/components/Auth/AuthProvider';
import { useArticleLimitStore } from '@/lib/stores/post';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { createClient } from '@/lib/supabase/component';

const ArticlePaywallBlock = dynamic(() => import('@/components/Auth/ArticlePaywallBlock'), {
  ssr: false
});

const cx = className.bind(styles);

export default function ContentWrapper(
  {
    content,
    className,
    headerContent = null,
    railContent = null,
    beforeContent = null,
    showAd = false,
    postPositionIndex = 0,
    isFullWidth = false,
    showPaywall = false
  }
) {
  const { user } = useAuth() || {};
  const {
    hasExceededLimit,
    initialize,
    trackArticleView,
  } = useArticleLimitStore();

  const router = useRouter();
  const supabase = createClient();
  const lightboxKey = useId();
  const setLightboxImages = useLightboxStore((state) => state.setImages);
  const removeLightboxImages = useLightboxStore((state) => state.removeImages);

  // Determine if this is a bot or not
  const [isHydrated, setIsHydrated] = useState(false);
  const [paywallEventSent, setPaywallEventSent] = useState(false);

  // Capture article slug in a ref to prevent race conditions
  const articleSlugRef = useRef(router.asPath);
  useEffect(() => {
    articleSlugRef.current = router.asPath;
  }, [router.asPath]);

  // Handle hydration and bot detection
  useEffect(() => {
    // Mark as hydrated and detect if bot
    setIsHydrated(true);
  }, []);

  // Sync lightbox images to the store after render
  const lightboxImagesRef = useRef([]);
  useEffect(() => {
    setLightboxImages(lightboxKey, lightboxImagesRef.current);
    return () => removeLightboxImages(lightboxKey);
  }, [content, lightboxKey, setLightboxImages, removeLightboxImages]);

  // Calculate shouldShowFullContent at component level for hook dependencies
  // Always show full content during SSR/before hydration to avoid hydration mismatch
  const shouldShowFullContent = (
    !isHydrated ||
    !showPaywall ||
    !hasExceededLimit() ||
    user ||
    isFromOrganicSearch()
  );

  // Initialize article limit tracking after hydration
  useEffect(() => {
    if (!isHydrated || user || !showPaywall) {
      return;
    }

    // Capture slug at effect start to prevent race condition
    const currentSlug = articleSlugRef.current;

    // Defer to next tick to avoid hydration issues
    const timer = setTimeout(() => {
      startTransition(() => {
        initialize(supabase)
          .then(() => {
            // Track this article view with captured slug
            trackArticleView(supabase, currentSlug);
          })
          .catch((error) => {
            console.error('Failed to initialize article limit tracking:', error);
          });
      });
    }, 0);

    return () => clearTimeout(timer);
  }, [isHydrated, user, showPaywall, supabase, initialize, trackArticleView]);

  // Track registration wall shown event - moved to top level to fix hooks violation
  useEffect(() => {
    if (typeof window !== 'undefined' && !shouldShowFullContent && !paywallEventSent) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'event': 'registration_wall_shown'
      });
      setPaywallEventSent(true);
    }
  }, [shouldShowFullContent, paywallEventSent]);

  const renderContentWithPaywall = () => {
    if (shouldShowFullContent) {
      const { result, images } = getReactiveContent({
        htmlString: content,
        applyAutoP: false,
        applyImgWrapper: true,
        applyAd: showAd,
        applyAdPostPositionIndex: postPositionIndex,
      });
      lightboxImagesRef.current = images;
      return result;
    }

    // Split content into paragraphs - show first 3 normally, hide the rest with inline CSS
    const paragraphs = content.split('</p>');
    const firstThreeParagraphs = paragraphs.slice(0, 3).join('</p>') + (paragraphs.length > 3 ? '</p>' : '');
    const remainingParagraphs = paragraphs.length > 3 ? paragraphs.slice(3).join('</p>') + '</p>' : '';

    const combinedContent = firstThreeParagraphs + (remainingParagraphs ? `<div class="mom-paywall" style="display: none">${remainingParagraphs}</div>` : '');

    const { result, images } = getReactiveContent({
      htmlString: combinedContent,
      applyAutoP: false,
      applyImgWrapper: true,
      applyAd: false,
      applyAdPostPositionIndex: postPositionIndex,
    });
    lightboxImagesRef.current = images;

    return (
      <>
        {result}
        <ArticlePaywallBlock />
      </>
    );
  };

  return (
    <article className={twMerge(cx(
      { 'mx-auto max-w-[var(--mom--content--max-width)]': !isFullWidth },
      className,
    ))}>
      <Suspense fallback={<Loading />}>
        {headerContent}

        <div className="flex flex-nowrap">
          <div className={cx(
            'component',
            `flex-1 min-w-0`,
          )}>
            {beforeContent}

            {renderContentWithPaywall()}
          </div>

          {!!railContent && (
            <aside className={classNames(`w-[100px] shrink-0 hidden md:block`)}>
              {railContent}
            </aside>
          )}
        </div>
      </Suspense>
    </article>
  );
};

ContentWrapper.propTypes = {
  content: PropTypes.any,
  children: PropTypes.node,
  className: PropTypes.string,
  isFullWidth: PropTypes.bool,
  showAd: PropTypes.bool,
  showPaywall: PropTypes.bool,
  railContent: PropTypes.object,
  beforeContent: PropTypes.object,
  postPositionIndex: PropTypes.number
};
